import { httpsCallable } from 'firebase/functions';
import { functions } from '../config';
import { uploadBytes, ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../config';
import { postInput } from '../firestore/input';
import { addDoc, collection, updateDoc } from 'firebase/firestore';
import { db } from '../config';
import { processingStatus, resultStatus, ocrStatus } from '../../../constants/enum';

interface FileUploadData {
  filePath: string;
  fileName: string;
  fileSize: number;
  fileType: string;
}

interface InputResultData {
  inputId: string;
  resultId: string;
}

// Firebase Storageにファイルを直接アップロードする
const uploadFilesToStorage = async (files: File[], userId: string): Promise<FileUploadData[]> => {
  const uploadPromises = files.map(async file => {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const filePath = `temp-uploads/${userId}/${fileName}`;

    const storageRef = ref(storage, filePath);
    await uploadBytes(storageRef, file);

    return {
      filePath,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    };
  });

  return Promise.all(uploadPromises);
};

// フロントエンドでInput（オリジナルファイル）を先に登録する
const createInputsWithOriginalFiles = async (
  files: File[],
  userId: string,
  groupId: string | null = null,
): Promise<InputResultData[]> => {
  const inputResultPromises = files.map(async file => {
    // ファイルをStorageにアップロード
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const filePath = `uploads/${userId}/temp/${fileName}`;

    const storageRef = ref(storage, filePath);
    await uploadBytes(storageRef, file);

    // ダウンロードURLを取得
    const downloadUrl = await getDownloadURL(storageRef);

    // オリジナルファイル情報
    const originalFileData = {
      file_path: filePath,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      file_url: downloadUrl,
    };

    // inputドキュメントを作成
    const inputData = {
      original_files: [originalFileData],
      files: [], // 変換後ファイルは後で追加
      groupId,
      created_at: new Date(),
      updated_at: new Date(),
      created_by: userId,
      updated_by: userId,
    };

    const inputDocRef = await postInput(inputData);
    const inputId = inputDocRef.id;

    // inputIdをドキュメントに追加
    await updateDoc(inputDocRef, { inputId });

    // resultドキュメントを作成（空のデータで）
    const resultData = {
      inputId: inputId,
      groupId,
      status: resultStatus.valid,
      processing_status: processingStatus.processing,
      ocr_status: ocrStatus.processing, // processing
      result_data: {
        mansionName: { value: '', id: [] },
        propertyAddress: { value: '', id: [] },
        roomNumber: { value: '', id: [] },
        establishmentDate: { value: '', id: [] },
        debtorAddress: { value: '', id: [] },
        debtorName: { value: '', id: [] },
        mortgageeCompanyName: { value: '', id: [] },
        loanAmount: { value: 0, id: [] },
        interestRate: { value: 0, id: [] },
        candidateCompanies: { value: '', id: [] },
        urls: null,
        candidateCompaniesByGemini: { value: '', id: [] },
        urlByGemini: null,
      },
      created_at: new Date(),
      updated_at: new Date(),
      created_by: userId,
      updated_by: userId,
    };

    const resultsRef = collection(db, 'results');
    const resultDocRef = await addDoc(resultsRef, resultData);
    const resultId = resultDocRef.id;

    // resultIdをドキュメントに追加
    await updateDoc(resultDocRef, { resultId });

    return { inputId, resultId };
  });

  return Promise.all(inputResultPromises);
};

// getOcrText関数を実行する
export const handleOcrFunctions = async (
  files: File[],
  userId: string,
  groupId: string | null = null,
): Promise<void> => {
  try {
    // 1. 先にInput（オリジナルファイル）を登録し、即座に一覧に反映
    const inputResults = await createInputsWithOriginalFiles(files, userId, groupId);

    // 2. Storage用にファイルをアップロード（OCR処理用）
    const uploadedFiles = await uploadFilesToStorage(files, userId);

    // 3. 各ファイルごとにOCR処理を実行
    const getOcrText = httpsCallable(functions, 'getOcrText');

    // すべてのファイルのOCR処理開始を並列で実行
    const ocrPromises = uploadedFiles.map((uploadedFile, index) => {
      const inputResult = inputResults[index];
      return getOcrText({
        uploadedFiles: [uploadedFile], // 1ファイルずつ処理
        inputResults: [inputResult], // 対応するinputResultを送信
      });
    });

    // すべてのOCR処理の開始を待機
    await Promise.all(ocrPromises);

    // eslint-disable-next-line no-console
    console.log(`${files.length}件のOCR処理を開始しました`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('OCR処理の開始に失敗しました:', error);
    throw error;
  }
};
